// codeModeAst - what a code-mode cell actually called, by evaluating its
// program instead of pattern-matching its text.
//
// A code-mode agent does not call tools at the wire boundary. It emits one JS
// program that calls `tools.exec_command({...})`, and the only way to know what
// the agent DID is to work out what that program computed. The existing reader
// (traceCodeModeCells) is already an abstract interpreter for exactly this
// reason - it propagates constant bindings, unrolls loops over known arrays,
// resolves template splices - but it runs on MASKED TEXT, so it can only
// interpret the shapes that survive masking.
//
// The difference that costs the most is string concatenation, which is the
// most common way a program builds a command. Measured against the text
// reader:
//
//   { cmd: "ls -la" }                    both resolve it
//   `ls ${dir}/${name}`  (identifiers)   both resolve it
//   `ls ${cfg.dir}`      (member)        text reader: empty args
//   "cat " + f           (in a loop)     text reader: {cmd: "cat "}
//   "curl https://" + h  (in .map)       text reader: {cmd: "curl https://"}
//   "ls " + parts.join("/")              text reader: {cmd: "ls "}
//
// Those are not refusals. The text reader parses the first literal, drops the
// rest of the expression, and returns a TRUNCATED value - a fragment shaped
// like a command. It is marked unresolved so nothing renders it, but the loop
// unrolling never fires either, because unrolling only runs once the argument
// resolves.
//
// On a tree each of those is one small case: a `binary_expression` with `+`,
// a `member_expression` against a known object, a `call_expression` on a known
// array's `join`. That is the whole argument for parsing here - not tidiness,
// but that the simulation can finally reach the expressions agents actually
// write.
//
// NOTHING IS EXECUTED, EVER. The cells are model-authored, tenant-supplied
// code. This reads a tree and folds constants; there is no eval, no Function,
// no sandbox, and every evaluator case is total - an unknown node yields
// UNRESOLVED rather than throwing or guessing.
import { grammarReady, parseSourceSync } from "./syntax/parserHost.js";

// A sentinel rather than undefined, so "evaluated to undefined" and "could not
// be evaluated" stay distinguishable - the difference between a value the
// program computed and one this reader could not follow.
const UNRESOLVED = Symbol("unresolved");
// An object that folded some of its entries. Carried as a wrapper so a caller
// can tell "nothing readable here" from "this much, and no more".
const PARTIAL = Symbol("partial");
export const isResolved = (v) => v !== UNRESOLVED && !(v && typeof v === "object" && v[PARTIAL]);
const partialValue = (v) => (v && typeof v === "object" && v[PARTIAL]) || null;

// Bounds. A cell is a short program; these keep a generated or pathological one
// from turning one trace into a workload.
// The caps are the text reader's, deliberately: they are part of the
// behavioural contract its suite pins, and changing what a cell yields while
// changing HOW it is read would make a regression indistinguishable from an
// intended difference.
const MAX_CALLS = 48;
const MAX_UNROLL = 16;
const MAX_DEPTH = 12;
const MAX_STRING = 8_000;
const MAX_BINDINGS = 64;
const MAX_ARRAY = 256;

const TOOLS_OBJECT = "tools";

const ESCAPES = { n: "\n", t: "\t", r: "\r", b: "\b", f: "\f", v: "\v", 0: "\0" };

/** Resolve backslash escapes in a string body or a lone escape sequence. */
function unescape(body) {
  return String(body).replace(/\\(u\{([0-9a-fA-F]+)\}|u([0-9a-fA-F]{4})|x([0-9a-fA-F]{2})|([\s\S]))/g,
    (_m, _all, brace, u4, x2, ch) => {
      if (brace) return String.fromCodePoint(parseInt(brace, 16));
      if (u4) return String.fromCharCode(parseInt(u4, 16));
      if (x2) return String.fromCharCode(parseInt(x2, 16));
      // A backslash before a newline is a line continuation: the pair
      // produces nothing, not a newline.
      if (ch === "\n") return "";
      return ESCAPES[ch] ?? ch;
    });
}

/** Cooked value of a quoted string node. */
function stringValue(node) {
  return unescape(node.text.slice(1, -1));
}

const cap = (s) => (typeof s === "string" && s.length > MAX_STRING ? s.slice(0, MAX_STRING) : s);

/**
 * Fold one expression node to a value, or UNRESOLVED.
 *
 * Total by construction: every unhandled node type falls through to
 * UNRESOLVED. A reader that threw on an unfamiliar expression would turn one
 * unusual cell into a blank trace.
 */
function evaluate(node, env, depth = 0) {
  if (!node || depth > MAX_DEPTH) return UNRESOLVED;
  switch (node.type) {
    case "string":
      return stringValue(node);
    case "number": {
      const n = Number(node.text);
      return Number.isFinite(n) ? n : UNRESOLVED;
    }
    case "true": return true;
    case "false": return false;
    case "null": return null;
    case "undefined": return undefined;
    case "identifier": {
      const found = env.get(node.text);
      return found === undefined && !env.has(node.text) ? UNRESOLVED : found;
    }
    case "parenthesized_expression":
      return evaluate(node.namedChild(0), env, depth + 1);
    case "template_string": {
      // Raw children, not named ones: the literal text between substitutions
      // is unnamed, and dropping it would splice the values together with the
      // text they were separated by removed.
      let out = "";
      for (let i = 0; i < node.childCount; i += 1) {
        const child = node.child(i);
        if (child.type === "template_substitution") {
          const value = evaluate(child.namedChild(0), env, depth + 1);
          if (!isResolved(value)) return UNRESOLVED;
          out += String(value);
        } else if (child.type === "string_fragment") {
          out += child.text;
        } else if (child.type === "escape_sequence") {
          out += unescape(child.text);
        }
      }
      return cap(out);
    }
    case "object": {
      // An entry this reader cannot fold does not bail the object: the other
      // entries are still what the program passed, and a tool argument is
      // usually readable on the strength of one field. The object carries what
      // it could fold and the CALL is reported unresolved, so nothing treats a
      // partial argument as complete.
      const out = {};
      let whole = true;
      for (let i = 0; i < node.namedChildCount; i += 1) {
        const pair = node.namedChild(i);
        if (pair.type === "shorthand_property_identifier") {
          if (env.has(pair.text)) out[pair.text] = env.get(pair.text);
          else whole = false;
          continue;
        }
        if (pair.type !== "pair") { whole = false; continue; }
        const keyNode = pair.childForFieldName("key");
        let key;
        if (keyNode.type === "string") key = stringValue(keyNode);
        else if (keyNode.type === "computed_property_name") {
          // `[k]: v` under an unreadable `k` must not mint a key spelled
          // "[k]" - an invented name stated as fact. Folded when the inner
          // expression folds to a primitive, dropped into the partial
          // otherwise.
          const inner = evaluate(keyNode.namedChild(0), env, depth + 1);
          if (!isResolved(inner) || (typeof inner !== "string" && typeof inner !== "number")) {
            whole = false;
            continue;
          }
          key = String(inner);
        } else key = keyNode.text;
        const value = evaluate(pair.childForFieldName("value"), env, depth + 1);
        if (isResolved(value)) out[key] = value;
        else whole = false;
      }
      return whole ? out : { [PARTIAL]: out };
    }
    case "array": {
      // Over the cap the value is refused, not truncated: a 300-element array
      // folded to its first 256 is a WRONG value, and a joined command built
      // from it would be stated as fact.
      if (node.namedChildCount > MAX_ARRAY) return UNRESOLVED;
      const out = [];
      for (let i = 0; i < node.namedChildCount; i += 1) {
        const value = evaluate(node.namedChild(i), env, depth + 1);
        if (!isResolved(value)) return UNRESOLVED;
        out.push(value);
      }
      return out;
    }
    case "unary_expression": {
      if (node.childForFieldName("operator")?.text !== "-") return UNRESOLVED;
      const operand = evaluate(node.childForFieldName("argument"), env, depth + 1);
      return typeof operand === "number" ? -operand : UNRESOLVED;
    }
    case "binary_expression": {
      const op = node.child(1)?.text;
      if (op !== "+") return UNRESOLVED;
      const left = evaluate(node.childForFieldName("left"), env, depth + 1);
      const right = evaluate(node.childForFieldName("right"), env, depth + 1);
      if (!isResolved(left) || !isResolved(right)) return UNRESOLVED;
      if (typeof left === "number" && typeof right === "number") return left + right;
      // Only strings and numbers concatenate the way a template would.
      // `1 + true` is 2 in real JS, not "1true"; refusing the other operand
      // types keeps the fold from stating a value the program never built.
      const stringish = (v) => typeof v === "string" || typeof v === "number";
      if (!stringish(left) || !stringish(right)) return UNRESOLVED;
      return cap(`${left}${right}`);
    }
    // Member access is deliberately NOT folded. A property read can reach a
    // value this reader never saw assigned, and the established contract keeps
    // splices to bare constants for that reason. Widening it is available and
    // is a separate decision, not a side effect of changing parsers.
    case "call_expression": {
      // Only the pure string/array builders an agent uses to assemble a
      // command. Anything else may have effects this reader must not imagine.
      const fn = node.childForFieldName("function");
      if (fn?.type !== "member_expression") return UNRESOLVED;
      const target = evaluate(fn.childForFieldName("object"), env, depth + 1);
      const method = fn.childForFieldName("property")?.text;
      if (!isResolved(target)) return UNRESOLVED;
      const args = node.childForFieldName("arguments");
      const argValues = [];
      for (let i = 0; i < (args?.namedChildCount || 0); i += 1) {
        argValues.push(evaluate(args.namedChild(i), env, depth + 1));
      }
      if (argValues.some((v) => !isResolved(v))) return UNRESOLVED;
      if (Array.isArray(target) && method === "join") {
        // Real join stringifies its separator: `join(null)` separates with
        // "null", not ",". Absent means comma; a non-primitive separator is
        // refused rather than guessed at.
        if (argValues.length === 0 || argValues[0] === undefined) return cap(target.join(","));
        const sep = argValues[0];
        if (typeof sep !== "string" && typeof sep !== "number" && typeof sep !== "boolean" && sep !== null) return UNRESOLVED;
        return cap(target.join(String(sep)));
      }
      if (typeof target === "string") {
        if (method === "trim") return target.trim();
        if (method === "toUpperCase") return target.toUpperCase();
        if (method === "toLowerCase") return target.toLowerCase();
        // `replace` substitutes the FIRST occurrence only; `replaceAll` every
        // one. Folding both as split/join made `"a-b-c".replace("-", "_")`
        // resolve to `a_b_c` where the program produced `a_b-c` - a command
        // this reader would then state as fact, in a feature whose whole point
        // is that the reconstruction is what actually ran.
        if ((method === "replace" || method === "replaceAll")
          && argValues.length === 2 && typeof argValues[0] === "string") {
          const search = argValues[0];
          const to = String(argValues[1]);
          // Real replace runs GetSubstitution over the replacement even for a
          // string search: `$&`, `$$`, `$'` all substitute. A `$` anywhere in
          // the replacement is refused rather than emulated - the fold below
          // would keep it literal and state a command that never ran.
          if (to.includes("$")) return UNRESOLVED;
          if (method === "replaceAll") {
            // An EMPTY search is the one case split/join gets wrong: real
            // `replaceAll("")` also inserts at the start and the end, which
            // split/join does not. Refused rather than emulated - nobody
            // builds a command that way, and a fold that is nearly right is
            // worse here than one that declines.
            if (search === "") return UNRESOLVED;
            return cap(target.split(search).join(to));
          }
          const at = target.indexOf(search);
          if (at < 0) return target;
          // An empty search matches at position 0 in real JS, which the
          // slice arithmetic below handles without a special case.
          return cap(target.slice(0, at) + to + target.slice(at + search.length));
        }
      }
      return UNRESOLVED;
    }
    default:
      return UNRESOLVED;
  }
}

// Array methods that change the value in place. A binding one of these touches
// is not the value its initializer stated any more.
const MUTATORS = new Set(["push", "pop", "shift", "unshift", "splice", "sort", "reverse", "fill", "copyWithin"]);

// Nodes that bind parameter names. Their names must register as declarations,
// because "declared twice means poisoned" is the whole scope model: a
// `function run(cmd)` whose param the walk cannot see leaves an outer
// `const cmd` looking like the only `cmd` in the program, and every use inside
// the function folds to the OUTER value - a command the agent never ran.
const PARAMETER_BINDERS = new Set([
  "function_declaration", "function_expression", "generator_function",
  "generator_function_declaration", "arrow_function", "method_definition",
]);

/** Every name a binding pattern introduces, conservatively: identifiers in
 * value position (defaults) are swept in too, since over-poisoning a name
 * costs an unresolved fold, never a wrong one. */
function patternNames(node, out) {
  if (!node) return out;
  if (node.type === "identifier" || node.type === "shorthand_property_identifier_pattern") {
    out.push(node.text);
    return out;
  }
  for (let i = 0; i < node.namedChildCount; i += 1) patternNames(node.namedChild(i), out);
  return out;
}

/** The identifier a member/subscript chain hangs off, or null. */
function memberRoot(node) {
  let current = node;
  while (current && (current.type === "member_expression" || current.type === "subscript_expression")) {
    current = current.childForFieldName("object");
  }
  return current?.type === "identifier" ? current.text : null;
}

/**
 * Bindings whose value the whole program agrees on.
 *
 * A name is only usable if it is declared exactly once, never reassigned, and
 * never mutated. Scope is deliberately NOT modelled: a name declared twice is
 * refused outright rather than resolved per block, because deciding which
 * declaration reaches a call means modelling control flow, and a reader that
 * guesses wrong states a command the agent never ran.
 */
function collectBindings(root, env) {
  const declared = new Map();
  const poisoned = new Set();
  // A name bound with no readable value: a parameter, a loop variable, a
  // catch binding, a destructured field. It counts as a declaration - so a
  // second binding of the same name poisons it - but never enters the
  // environment, which is also exactly the state loop unrolling needs: an
  // absent name is the one it can bind per element.
  const declare = (name, valueNode = null) => {
    if (declared.has(name)) poisoned.add(name);
    else declared.set(name, valueNode);
  };
  const stack = [root];
  while (stack.length) {
    const node = stack.pop();
    if (node.type === "variable_declarator") {
      const name = node.childForFieldName("name");
      if (name?.type === "identifier") declare(name.text, node.childForFieldName("value") ?? null);
      else for (const bound of patternNames(name, [])) declare(bound);
    } else if (PARAMETER_BINDERS.has(node.type)) {
      const name = node.childForFieldName("name");
      if (name?.type === "identifier") declare(name.text);
      for (const bound of patternNames(node.childForFieldName("parameters") || node.childForFieldName("parameter"), [])) {
        declare(bound);
      }
    } else if (node.type === "for_in_statement") {
      for (const bound of patternNames(node.childForFieldName("left"), [])) declare(bound);
    } else if (node.type === "catch_clause") {
      for (const bound of patternNames(node.childForFieldName("parameter"), [])) declare(bound);
    } else if (node.type === "assignment_expression" || node.type === "augmented_assignment_expression") {
      const target = node.childForFieldName("left");
      if (target?.type === "identifier") poisoned.add(target.text);
      else {
        // `req.path = x` leaves `req` bound to a value its initializer no
        // longer describes.
        const rootName = memberRoot(target);
        if (rootName) poisoned.add(rootName);
      }
    } else if (node.type === "update_expression") {
      const target = node.childForFieldName("argument");
      if (target?.type === "identifier") poisoned.add(target.text);
      else {
        const rootName = memberRoot(target);
        if (rootName) poisoned.add(rootName);
      }
    } else if (node.type === "call_expression") {
      const fn = node.childForFieldName("function");
      if (fn?.type === "member_expression" && MUTATORS.has(fn.childForFieldName("property")?.text)) {
        const target = fn.childForFieldName("object");
        if (target?.type === "identifier") poisoned.add(target.text);
      }
    }
    for (let i = node.namedChildCount - 1; i >= 0; i -= 1) stack.push(node.namedChild(i));
  }

  // Folded in declaration order so an initializer can use an earlier binding.
  const ordered = [...declared.entries()].filter(([name]) => !poisoned.has(name));
  for (const [name, valueNode] of ordered) {
    if (env.size >= MAX_BINDINGS) break;
    if (!valueNode) continue;
    const folded = evaluate(valueNode, env);
    // A binding that cannot be folded is recorded as absent, not as undefined:
    // `env.has` is what tells an identifier apart from a value this reader
    // could not follow.
    if (isResolved(folded)) env.set(name, folded);
  }
  return env;
}

const isToolsCall = (node) => {
  if (node.type !== "call_expression") return null;
  const fn = node.childForFieldName("function");
  if (fn?.type !== "member_expression") return null;
  if (fn.childForFieldName("object")?.text !== TOOLS_OBJECT) return null;
  return fn.childForFieldName("property")?.text || null;
};

/**
 * The loop or map a node sits inside, when that loop iterates a known array.
 *
 * Only ONE containing loop is honoured, for the reason the text reader already
 * documents: a call under two known loops runs outer x inner times, and
 * unrolling along a single axis would state fewer calls than ran.
 */
function containingIteration(node, env) {
  const found = [];
  for (let cur = node.parent; cur; cur = cur.parent) {
    if (cur.type === "for_in_statement") {
      // The node covers both loops; only `of` iterates the VALUES. `for..in`
      // over an array walks its index strings, so unrolling it with elements
      // would state calls the program never made.
      const param = cur.childForFieldName("left");
      const source = evaluate(cur.childForFieldName("right"), env);
      if (cur.childForFieldName("operator")?.text === "of"
        && param?.type === "identifier" && Array.isArray(source)) {
        found.push({ param: param.text, elements: source.slice(0, MAX_UNROLL) });
      }
    } else if (cur.type === "call_expression") {
      const fn = cur.childForFieldName("function");
      if (fn?.type === "member_expression"
        && ["map", "forEach", "flatMap", "filter"].includes(fn.childForFieldName("property")?.text)) {
        const source = evaluate(fn.childForFieldName("object"), env);
        const callback = cur.childForFieldName("arguments")?.namedChild(0);
        const param = callback?.childForFieldName("parameter")
          || callback?.childForFieldName("parameters")?.namedChild(0);
        if (Array.isArray(source) && param?.type === "identifier") {
          found.push({ param: param.text, elements: source.slice(0, MAX_UNROLL) });
        }
      }
    }
  }
  return found.length === 1 ? found[0] : null;
}

/**
 * Every `tools.X(...)` a cell called, with its arguments as the program
 * computed them.
 *
 * @returns the same shape traceCodeModeCells' text reader returns, or null
 *   when the JavaScript grammar is not loaded yet - the caller runs inside a
 *   synchronous memo and needs a straight answer, not a promise.
 */
export function extractCellCallsAst(source) {
  const src = String(source || "");
  if (!src.includes(`${TOOLS_OBJECT}.`)) return [];
  if (!grammarReady("javascript")) return null;
  const parsed = parseSourceSync("javascript", src);
  if (!parsed) return null;

  const env = collectBindings(parsed.root, new Map());
  const calls = [];
  const stack = [parsed.root];
  const found = [];
  while (stack.length) {
    const node = stack.pop();
    const tool = isToolsCall(node);
    if (tool) found.push({ tool, node });
    for (let i = node.namedChildCount - 1; i >= 0; i -= 1) stack.push(node.namedChild(i));
  }
  found.sort((a, b) => a.node.startIndex - b.node.startIndex);

  for (const { tool, node } of found) {
    if (calls.length >= MAX_CALLS) break;
    const args = node.childForFieldName("arguments");
    const first = args?.namedChild(0) ?? null;
    const argText = first ? first.text : "";
    const value = first ? evaluate(first, env) : {};

    if (isResolved(value)) {
      calls.push({ tool, args: value, argsResolved: true, argText });
      continue;
    }
    const partial = partialValue(value);
    // Unresolved from the top-level environment may still resolve once per
    // element of the loop it sits in - which is what the agent actually ran.
    const iteration = containingIteration(node, env);
    if (iteration) {
      let unrolled = 0;
      for (const element of iteration.elements) {
        const bound = new Map(env);
        bound.set(iteration.param, element);
        const perElement = evaluate(first, bound);
        if (!isResolved(perElement)) continue;
        calls.push({ tool, args: perElement, argsResolved: true, argText, unrolled });
        unrolled += 1;
        if (calls.length >= MAX_CALLS) break;
      }
      if (unrolled) continue;
    }
    // `null`, not `{}`: an argument nothing could be read from is not an empty
    // object, and a caller that cannot tell them apart renders a call as
    // having been made with no arguments.
    calls.push({ tool, args: partial, argsResolved: false, argText });
  }
  return calls;
}
